import type {
  ActingParty,
  ConveyedPersonAction,
  PersonInvolvement,
  PersonRecordCreators,
} from "../types/involvement.js";
import { administeringInvolvementKind } from "./administeringInvolvementKind.js";
import { isInvolvementLive } from "./isInvolvementLive.js";
import { isPartyActingAsOrganisationAdmin } from "./isPartyActingAsOrganisationAdmin.js";
import { organisationsThatCreatedPersonRecords } from "./organisationsThatCreatedPersonRecords.js";

const administrationActions: readonly ConveyedPersonAction[] = [
  "read",
  "read_log",
  "update",
  "merge",
  "assign",
  "allocate_responsible_user",
];

/**
 * What organisation administration of a person conveys to a party acting as an
 * organisation admin: reading the person and their log, correcting the person
 * and merging their records, and deciding who is involved. Never anything about
 * assessments, which an organisation admin reads only as anybody else would.
 *
 * An organisation administers the persons it holds the duty of care for, and
 * the persons it created a person record of while no organisation holds one,
 * so every person has somebody able to manage them from the moment their
 * record is created. A duty of care held by a member organisation carries up to every
 * organisation above it; having created a person record does not.
 */
export const personActionsFromOrganisationAdministration = (
  person: PersonRecordCreators,
  involvements: readonly Pick<
    PersonInvolvement,
    "kind" | "party" | "endedAt" | "expiresAt"
  >[],
  party: Pick<
    ActingParty,
    "organisationId" | "roles" | "memberOrganisationIds"
  >,
  now: Date = new Date(),
): ConveyedPersonAction[] => {
  if (!isPartyActingAsOrganisationAdmin(party)) {
    return [];
  }

  const duties = involvements.filter(
    (involvement) =>
      involvement.kind === administeringInvolvementKind &&
      isInvolvementLive(involvement, now),
  );
  const accountable = [
    party.organisationId,
    ...(party.memberOrganisationIds ?? []),
  ];
  const holdsTheDuty = duties.some(
    (duty) =>
      duty.party.kind === "organisation" &&
      accountable.includes(duty.party.organisationId),
  );
  const openedItAndNobodyHoldsTheDuty =
    duties.length === 0 &&
    organisationsThatCreatedPersonRecords(person).includes(
      party.organisationId,
    );

  return holdsTheDuty || openedItAndNobodyHoldsTheDuty
    ? [...administrationActions]
    : [];
};
